import { getLogger } from '@spotify-internal/remote-config-common';
import { getDefinition, defineInternal, findPropertyRef, isInternalPropertyRef, } from './define';
const JSON_SCHEMA_VERSION = '1';
defineInternal({
    name: '#v',
    description: 'Schema version of JSON serialized configuration.',
    default: '',
});
export var Configuration;
(function (Configuration) {
    class DefaultConfiguration {
        constructor(entries = [], accessListeners = new Set()) {
            this.values = new Map(entries);
            this.accessListeners = accessListeners;
        }
        getValue(ref) {
            const property = getDefinition(ref);
            if (!isInternalPropertyRef(ref)) {
                for (const accessListener of this.accessListeners) {
                    try {
                        accessListener(ref, this);
                    }
                    catch (error) {
                        getLogger('properties').warn('Error invoking access listener.', error);
                    }
                }
            }
            if (this.values.has(ref)) {
                return this.values.get(ref);
            }
            return property.default;
        }
        toJSON(options = {}) {
            const json = {};
            if (!options.omitInternal)
                json['#v'] = JSON_SCHEMA_VERSION;
            for (const [ref, value] of this.values) {
                const property = getDefinition(ref);
                if (options.omitDefaults && value === property.default)
                    continue;
                if (options.omitInternal && property.type === 'internal')
                    continue;
                json[property.name] = value;
            }
            return json;
        }
        toBuilder() {
            return new Builder(this.values, this.accessListeners);
        }
    }
    function parseEntries(json) {
        if (typeof json === 'string') {
            json = JSON.parse(json);
        }
        if (typeof json !== 'object' || json === null) {
            throw new TypeError('Expected object');
        }
        migrateSchema(json);
        const entries = new Map();
        for (const [key, value] of Object.entries(json)) {
            const ref = findPropertyRef(key);
            if (!ref)
                continue;
            entries.set(ref, value);
        }
        return entries;
    }
    function fromJSON(json) {
        return new DefaultConfiguration(parseEntries(json));
    }
    Configuration.fromJSON = fromJSON;
    function builder(from) {
        return (from === null || from === void 0 ? void 0 : from.toBuilder()) || new Builder();
    }
    Configuration.builder = builder;
    function merge(...configs) {
        return configs
            .map(config => config.toBuilder())
            .reduce((acc, next) => acc.merge(next), new Builder())
            .build();
    }
    Configuration.merge = merge;
    function empty() {
        return new DefaultConfiguration();
    }
    Configuration.empty = empty;
    class Builder extends DefaultConfiguration {
        setValues(other) {
            for (const [ref, value] of other) {
                this.setValue(ref, value);
            }
            return this;
        }
        withAccessListener(accessListener) {
            this.accessListeners.add(accessListener);
            return this;
        }
        setValue(ref, value) {
            getDefinition(ref);
            this.values.set(ref, value);
            return this;
        }
        merge(other) {
            for (const [ref, value] of other.values) {
                this.values.set(ref, value);
            }
            for (const listener of other.accessListeners) {
                this.accessListeners.add(listener);
            }
            return this;
        }
        getValue(ref) {
            const property = getDefinition(ref);
            if (this.values.has(ref)) {
                return this.values.get(ref);
            }
            return property.default;
        }
        build() {
            return new DefaultConfiguration(this.values, this.accessListeners);
        }
        [Symbol.iterator]() {
            return this.values.entries();
        }
        static fromJSON(json) {
            return new Builder(parseEntries(json));
        }
    }
    Configuration.Builder = Builder;
    class Delegate {
        constructor(provider) {
            this.provider = provider;
        }
        getValue(ref) {
            return this.provider().getValue(ref);
        }
        toJSON(options) {
            return this.provider().toJSON(options);
        }
        toBuilder() {
            return this.provider().toBuilder();
        }
    }
    Configuration.Delegate = Delegate;
})(Configuration || (Configuration = {}));
function migrateSchema(json) {
    if (json['#v'] === undefined) {
        json['#v'] = '1';
        if ('configurationAssignmentId' in json) {
            json['#configurationAssignmentId'] = json.configurationAssignmentId;
            delete json.configurationAssignmentId;
        }
        if ('rcsFetchTime' in json) {
            json['#fetchTimeMillis'] = Number(json.rcsFetchTime);
            delete json.rcsFetchTime;
        }
        if (typeof json.properties === 'object') {
            const groupIds = {};
            json['#groupIds'] = groupIds;
            for (const [name, { value, groupId }] of Object.entries(json.properties)) {
                json[name] = value;
                if (groupId) {
                    groupIds[name] = Number(groupId);
                }
            }
            delete json.properties;
        }
        delete json.clientFetchTime;
    }
    delete json['#v'];
}
//# sourceMappingURL=configuration.js.map